import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {createSelector, wrapInShallowSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AiReview} from '../../../../../helpers/models/conversationAiHelpers';
import {AnnotationAiTagSuggestionModel} from '../../../../../models/api/annotationModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {getTags} from '../../../../../store/data/tags/tagsSelectors';
import {AiTagSuggestionControls} from './aiTagSuggestionControls';
import {CommentBodyAiTagSuggestionTagPill} from './commentBodyAiTagSuggestionTagPill';

/*
 * Props.
 */

interface CommentBodyAiTagSuggestionProps {
  conversation: ConversationModel | undefined;
  aiSuggestion: AnnotationAiTagSuggestionModel;
  onAiReview: (review: AiReview) => void;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 12px 12px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const StyledTextDiv = styled.div`
  margin-top: 1px;
  line-height: 1.4;
  font-size: ${fontSizes.medium};
  margin-bottom: 5px;
`;

/*
 * Component.
 */

export const AiTagSuggestionCommentBody: FC<CommentBodyAiTagSuggestionProps> = (props) => {
  const {conversation, aiSuggestion, onAiReview} = props;

  // We can trust that the tag is in the store at this point because it's part of the comment unfurls.
  const getSuggestedTags = useMemo(
    () =>
      wrapInShallowSelector(
        createSelector([getTags], (tags) =>
          aiSuggestion.tagIds.map((tagId) => tags[tagId]).filter(isDefined),
        ),
      ),
    [aiSuggestion.tagIds],
  );
  const suggestedTags = useAppSelector(getSuggestedTags);

  return (
    <StyledContainerDiv>
      <StyledContentDiv>
        <StyledTextDiv>
          <Trans id="annotation-ai-tag-suggestion-call-to-action">Suggested based on message content:</Trans>
        </StyledTextDiv>
        <StyledTextDiv>
          {suggestedTags.map((tag) => (
            <CommentBodyAiTagSuggestionTagPill key={tag.id} tag={tag} />
          ))}
        </StyledTextDiv>
      </StyledContentDiv>
      <AiTagSuggestionControls
        conversation={conversation}
        aiSuggestion={aiSuggestion}
        onAiReview={onAiReview}
      />
    </StyledContainerDiv>
  );
};
